import { useMemo, useRef, useState, useEffect } from 'react';

interface OptionItem {
  id: string;
  label: string;
}

interface MultiDropdownChipsProps {
  options: OptionItem[];
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}

/** Prefix used for free-typed values that aren't in the options list. */
const FREE_PREFIX = '__free__:';

export function MultiDropdownChips({ options, values, onChange, placeholder }: MultiDropdownChipsProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  /** Label for a chip — handles both option IDs and free-text IDs. */
  const labelOf = (id: string) => {
    if (id.startsWith(FREE_PREFIX)) return id.slice(FREE_PREFIX.length);
    return options.find((o) => o.id === id)?.label ?? id;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => !values.includes(o.id) && (!q || o.label.toLowerCase().includes(q)),
    );
  }, [options, query, values]);

  /** True when typed text doesn't exactly match any option and isn't already added. */
  const hasFreeInput = useMemo(() => {
    const q = query.trim();
    if (!q) return false;
    const freeId = FREE_PREFIX + q;
    if (values.includes(freeId)) return false; // already added
    return !options.some((o) => o.label.toLowerCase() === q.toLowerCase());
  }, [options, query, values]);

  const totalItems = filtered.length + (hasFreeInput ? 1 : 0);

  const remove = (id: string) => onChange(values.filter((v) => v !== id));

  const addById = (id: string) => {
    onChange([...values, id]);
    setQuery('');
    setHighlight(0);
  };

  const addFreeText = () => {
    const text = query.trim();
    if (!text) return;
    addById(FREE_PREFIX + text);
  };

  return (
    <div ref={wrapperRef} className="relative">
      {values.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-3">
          {values.map((id) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => remove(id)}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 border-2 border-ink/15 hover:border-ink/40 transition px-3 py-1.5 text-sm font-semibold text-ink"
              >
                {labelOf(id)}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        inputMode="search"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(totalItems - 1, h + 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filtered[highlight]) {
              addById(filtered[highlight].id);
            } else if (hasFreeInput) {
              addFreeText();
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className="w-full rounded-2xl border border-ink-light/40 bg-white px-5 py-4 text-base outline-none focus:border-brand focus:ring-2 focus:ring-brand-100"
      />
      {open && (filtered.length > 0 || hasFreeInput) && (
        <ul
          role="listbox"
          className="absolute z-10 mt-2 w-full bg-white rounded-2xl border border-ink-light/30 shadow-cardLg max-h-72 overflow-auto"
        >
          {filtered.map((o, i) => (
            <li key={o.id}>
              <button
                type="button"
                role="option"
                aria-selected={highlight === i}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => addById(o.id)}
                className={`w-full text-left px-5 py-3 text-base ${
                  highlight === i ? 'bg-brand-50 text-ink' : 'text-ink hover:bg-brand-50'
                }`}
              >
                {o.label}
              </button>
            </li>
          ))}
          {hasFreeInput && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={highlight === filtered.length}
                onMouseEnter={() => setHighlight(filtered.length)}
                onClick={addFreeText}
                className={`w-full text-left px-5 py-3 text-base border-t border-ink-light/20 ${
                  highlight === filtered.length
                    ? 'bg-brand-50 text-ink'
                    : 'text-ink-soft'
                }`}
              >
                Add &ldquo;{query.trim()}&rdquo;
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
